/**
 * RootMigrationBanner — shown on a device detail page when the
 * connector reports running_as_root=1. Legacy install path; v1.2.0+
 * moved to user-level LaunchAgent / systemctl --user.
 *
 * Provides a copy button for the one-line install.sh invocation
 * that triggers the migration cleanly when run from the user's
 * terminal. Avoids remote sudo by design — see design memo at
 * docs/superpowers/specs/2026-05-28-user-level-agent-design.md.
 */

import { useState } from 'react';

const INSTALL_CMD = 'curl -sSL https://download.clawdgate.com/install.sh | sh';
const SETUP_CMD = '~/.local/bin/clawdgate-connector setup';

export default function RootMigrationBanner() {
  const [copiedInstall, setCopiedInstall] = useState(false);
  const [copiedSetup, setCopiedSetup] = useState(false);

  const copy = async (text: string, which: 'install' | 'setup') => {
    try {
      await navigator.clipboard.writeText(text);
      if (which === 'install') {
        setCopiedInstall(true);
        setTimeout(() => setCopiedInstall(false), 2000);
      } else {
        setCopiedSetup(true);
        setTimeout(() => setCopiedSetup(false), 2000);
      }
    } catch {
      // fall through; user can select-and-copy from the inline <code>
    }
  };

  return (
    <div className="mb-6 bg-red-50 dark:bg-red-900/20 border border-red-300 dark:border-red-700 rounded-xl p-4">
      <div className="flex items-start gap-3">
        <div className="p-2 bg-red-100 dark:bg-red-800/40 rounded-lg shrink-0">
          <svg className="w-5 h-5 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-red-900 dark:text-red-200 mb-1">
            Connector is running as <code>root</code> — migrate to user-level install
          </h3>
          <p className="text-sm text-red-700 dark:text-red-300 mb-3">
            This device is on the legacy system-level install (LaunchDaemon on macOS,
            system-level systemd on Linux), which runs the connector with root
            privileges. v1.3.0+ runs as your normal user account — fewer permissions,
            uses your real <code>~/.claude</code>, and Claude Code workspaces just work.
          </p>
          <p className="text-sm text-red-700 dark:text-red-300 mb-2">
            <strong>This is the same flow as a fresh install</strong> — just run on
            this device. The <code>setup</code> wizard auto-detects the legacy daemon
            and offers to migrate it.
          </p>
          <p className="text-xs text-red-700 dark:text-red-300 mb-2 mt-3 font-medium">
            Step 1 — download the binary:
          </p>
          <div className="flex items-center gap-2 bg-slate-900 dark:bg-slate-950 text-slate-100 rounded-lg p-3 mb-3 font-mono text-xs">
            <code className="flex-1 select-all break-all">{INSTALL_CMD}</code>
            <button
              type="button"
              onClick={() => copy(INSTALL_CMD, 'install')}
              className="shrink-0 px-2.5 py-1 text-xs rounded-md bg-slate-700 hover:bg-slate-600 text-slate-100 cursor-pointer"
            >
              {copiedInstall ? '✓ Copied' : 'Copy'}
            </button>
          </div>
          <p className="text-xs text-red-700 dark:text-red-300 mb-2 font-medium">
            Step 2 — run setup (prompts for sudo password once during the migrate step):
          </p>
          <div className="flex items-center gap-2 bg-slate-900 dark:bg-slate-950 text-slate-100 rounded-lg p-3 mb-3 font-mono text-xs">
            <code className="flex-1 select-all break-all">{SETUP_CMD}</code>
            <button
              type="button"
              onClick={() => copy(SETUP_CMD, 'setup')}
              className="shrink-0 px-2.5 py-1 text-xs rounded-md bg-slate-700 hover:bg-slate-600 text-slate-100 cursor-pointer"
            >
              {copiedSetup ? '✓ Copied' : 'Copy'}
            </button>
          </div>
          <p className="text-xs text-red-600 dark:text-red-400">
            Your <code>agent_id</code> + auth token are preserved by the migration — no
            need to re-register. After setup completes, the connector will run as your
            user account and this banner will disappear within ~30 seconds.
          </p>
        </div>
      </div>
    </div>
  );
}
